'use client';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { motion } from 'motion/react';
import { ArrowRight, Sparkles, Target } from 'lucide-react';
import Link from 'next/link';

interface LandingUseCasesProps {
  title?: string;
  useCases: string[];
  gradientFrom: string;
  gradientTo: string;
}

export function LandingUseCases({
  title = '适用场景',
  useCases,
  gradientFrom,
  gradientTo,
}: LandingUseCasesProps) {
  return (
    <section className="py-24 bg-white dark:bg-neutral-950 relative overflow-hidden">
      <div className="container mx-auto px-4 max-w-5xl relative z-10">
        <div className="flex flex-col md:flex-row items-center justify-between gap-12">
          <div className="flex-1 space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-neutral-100 dark:bg-neutral-800 text-sm font-medium text-neutral-600 dark:text-neutral-300">
                <Target className="w-4 h-4" style={{ color: gradientFrom }} />
                <span>精准适配多种场景</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white">
                {title}
              </h2>
              <p className="text-lg text-neutral-600 dark:text-neutral-400 max-w-lg">
                无论是学术研究、商务办公还是个人阅读，我们都能为您提供专业、准确的文档翻译服务。
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              {useCases.map((useCase, index) => (
                <motion.span
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className={cn(
                    'px-5 py-2.5 rounded-full text-sm font-medium',
                    'bg-neutral-50 dark:bg-neutral-900',
                    'border border-neutral-200 dark:border-neutral-800',
                    'text-neutral-700 dark:text-neutral-300',
                    'hover:scale-105 hover:shadow-md transition-all cursor-default',
                    'hover:border-transparent',
                  )}
                  style={
                    {
                      // We use a style object for hover color since we can't easily do dynamic hover classes with arbitrary colors
                    }
                  }
                >
                  {useCase}
                </motion.span>
              ))}
            </div>
          </div>

          <div className="shrink-0 w-full md:w-auto">
            <div className="relative p-8 rounded-3xl bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 text-center space-y-6 overflow-hidden group">
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-500"
                style={{
                  background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})`,
                }}
              />

              <div className="relative z-10">
                <div
                  className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4 shadow-lg"
                  style={{
                    background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})`,
                  }}
                >
                  <Sparkles className="w-8 h-8 text-white" />
                </div>

                <h3 className="text-xl font-bold text-neutral-900 dark:text-white">
                  准备好开始了吗？
                </h3>
                <p className="text-neutral-600 dark:text-neutral-400 max-w-xs mx-auto">
                  立即体验 AI 驱动的极速文档翻译，注册即送免费额度。
                </p>

                <Button
                  asChild
                  size="lg"
                  className="w-full h-12 rounded-full font-semibold text-white shadow-lg transition-transform hover:scale-105"
                  style={{
                    background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})`,
                  }}
                >
                  <Link href="/translate/upload">
                    立即翻译
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
